import {
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import RemoveCircleOutlineOutlinedIcon from "@material-ui/icons/RemoveCircleOutlineOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";
import UnfoldMoreOutlinedIcon from "@material-ui/icons/UnfoldMoreOutlined";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import RemoveOutlinedIcon from "@material-ui/icons/RemoveOutlined";
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { LENDER_REQUEST_CHANGE, LINE_ITEM_STATUS, REQUEST_STATUSES } from "../../../shared/constants/RequestStatuses";

import "./ReasonCodesReviewSection.css";

const reasonCodeComparer = (a, b) => (a.reasonCode || "").localeCompare(b.reasonCode || "");

/**
 * renderRequestedChangeIcon
 * Returns an icon and label for lenderRequestChange
 */
const renderRequestedChangeIcon = (lenderRequestChange) => (
  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    {lenderRequestChange === LENDER_REQUEST_CHANGE.ADDED ? (
      <>
        <AddOutlinedIcon style={{ fontSize: "18px", color: "#2f7a45" }} />
        <span>Added</span>
      </>
    ) : lenderRequestChange === LENDER_REQUEST_CHANGE.REMOVED ? (
      <>
        <RemoveOutlinedIcon style={{ fontSize: "18px", color: "#8f2a2a" }} />
        <span>Removed</span>
      </>
    ) : (
      <span>{lenderRequestChange}</span>
    )}
  </span>
);

/**
 * renderDecisionIcon
 * Returns an icon and label for decision status
 */
const renderDecisionIcon = (status) => (
  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    {status === LINE_ITEM_STATUS.ACCEPT ? (
      <>
        <CheckCircleOutlineOutlinedIcon style={{ fontSize: "18px", color: "#2f7a45" }} />
        <span>Accept</span>
      </>
    ) : status === LINE_ITEM_STATUS.REJECT ? (
      <>
        <ClearOutlinedIcon style={{ fontSize: "18px", color: "#8f2a2a" }} />
        <span>Reject</span>
      </>
    ) : status === LINE_ITEM_STATUS.MODIFY ? (
      <>
        <EditOutlinedIcon style={{ fontSize: "18px", color: "#8f4a00" }} />
        <span>Modify</span>
      </>
    ) : (
      <span>{status}</span>
    )}
  </span>
);

/**
 * mapReasonCodeOutcome
 *
 * Pure 6-rule mapping (verbatim from FEATURE_SPEC.md §8):
 *   Removed + Accept -> Remove from Final
 *   Added   + Accept -> Add to Final
 *   Removed + Reject -> Add to Final (retained)
 *   Added   + Reject -> Remove from Final (never added)
 *   Removed + Modify -> Remove from Final
 *   Added   + Modify -> Add to Final
 *
 * Returns true (Add to Final), false (Remove from Final), or undefined for unrecognized inputs.
 * Direction (lenderRequestChange) is always an explicit input, never re-derived here.
 */
export const mapReasonCodeOutcome = (lenderRequestChange, status) => {
  if (lenderRequestChange === LENDER_REQUEST_CHANGE.REMOVED) {
    return status === LINE_ITEM_STATUS.REJECT;
  }
  if (lenderRequestChange === LENDER_REQUEST_CHANGE.ADDED) {
    return status !== LINE_ITEM_STATUS.REJECT;
  }
  return undefined;
};

/**
 * computeFinalReasonCodeIds
 *
 * Applies mapReasonCodeOutcome to each decision. Active reason codes with no matching
 * decision entry pass through to Final Reason Codes unchanged.
 */
export const computeFinalReasonCodeIds = (activeReasonCodeIds, decisions) => {
  const finalIds = new Set(activeReasonCodeIds || []);

  (decisions || []).forEach(({ reasonId, lenderRequestChange, status }) => {
    const include = mapReasonCodeOutcome(lenderRequestChange, status);
    if (include === true) finalIds.add(reasonId);
    else if (include === false) finalIds.delete(reasonId);
  });

  return Array.from(finalIds);
};

const renderSectionHeader = (title, IconComponent, toneClass) => (
  <h3 className="request-details-subheader review-reason-codes-header" style={{ marginBottom: 10 }}>
    <span className={`review-reason-codes-header-icon ${toneClass}`} aria-hidden="true">
      <IconComponent fontSize="small" />
    </span>
    <span>{title}</span>
  </h3>
);

const renderReasonCodeList = (reasonCodes, emptyMessage, showHeader = true) => (
  !reasonCodes.length ? (
    <p className="no-reason-codes-message">{emptyMessage}</p>
  ) : (
    <Table className="review-reason-codes-table">
      {showHeader && (
        <TableHead>
          <TableRow>
            <TableCell className="reason-code-name-column">Reason Code</TableCell>
          </TableRow>
        </TableHead>
      )}
      <TableBody>
        {reasonCodes.map((reasonCode) => (
          <TableRow key={reasonCode.reasonId}>
            <TableCell className="reason-code-name-column">{reasonCode.reasonCode || "-"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const renderReasonCodeBadgeList = (reasonCodes, emptyMessage) => (
  !reasonCodes.length ? (
    <p className="no-reason-codes-message">{emptyMessage}</p>
  ) : (
    <div className="review-reason-codes-simple-list">
      {reasonCodes.map((reasonCode) => (
        <span key={reasonCode.reasonId} className="reason-code-badge">
          {reasonCode.reasonCode || "-"}
        </span>
      ))}
    </div>
  )
);

const buildAcceptedRequestedIds = (requestStatusChangeReasonCodes, statusName) => {
  const isDeclined = statusName === REQUEST_STATUSES.NO_ACTION_TAKEN || statusName === 'Declined';
  const acceptedIds = new Set();

  (requestStatusChangeReasonCodes || []).forEach((rc) => {
    if (rc.status === LINE_ITEM_STATUS.REJECT) {
      return;
    }

    if (rc.status === LINE_ITEM_STATUS.ACCEPT || rc.status === LINE_ITEM_STATUS.MODIFY) {
      acceptedIds.add(rc.reasonId);
      return;
    }

    if (!rc.status && !isDeclined) {
      acceptedIds.add(rc.reasonId);
    }
  });

  return acceptedIds;
};

/**
 * ReasonCodesReviewSection
 *
 * Renders Current / Requested-to-Remove / Requested-to-Add reason codes (always read-only),
 * plus a combined Final Reason Codes section: editable (Accept/Reject + reviewer add/remove)
 * while actively reviewing, static/read-only once decided (showDecisionStatus).
 */
const ReasonCodesReviewSection = ({
  activeReasonCodeIds,
  requestStatusChangeReasonCodes,
  reasonCodeOptions,
  showDecisionStatus,
  canReviewReasonCodes,
  onReasonCodeDecisionsChange,
  onFinalReasonCodesCountChange,
  persistedReasonCodeDecisions,
  statusName,
  isInternal
}) => {
  const reasonCodeMap = useMemo(
    () => new Map((reasonCodeOptions || []).map(rc => [rc.reasonId, rc])),
    [reasonCodeOptions]
  );

  const activeIdSet = useMemo(() => new Set(activeReasonCodeIds || []), [activeReasonCodeIds]);

  const currentReasonCodes = useMemo(() => (
    (activeReasonCodeIds || [])
      .map(id => reasonCodeMap.get(id))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [activeReasonCodeIds, reasonCodeMap]);

  const requestedRemoveReasonCodes = useMemo(() => (
    (requestStatusChangeReasonCodes || [])
      .filter(rc => rc.lenderRequestChange === LENDER_REQUEST_CHANGE.REMOVED && rc.status !== LINE_ITEM_STATUS.MODIFY)
      .map(rc => reasonCodeMap.get(rc.reasonId))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [requestStatusChangeReasonCodes, reasonCodeMap]);

  const requestedAddReasonCodes = useMemo(() => (
    (requestStatusChangeReasonCodes || [])
      .filter(rc => rc.lenderRequestChange === LENDER_REQUEST_CHANGE.ADDED && rc.status !== LINE_ITEM_STATUS.MODIFY)
      .map(rc => reasonCodeMap.get(rc.reasonId))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [requestStatusChangeReasonCodes, reasonCodeMap]);

  const [acceptedRequestedIds, setAcceptedRequestedIds] = useState(new Set());
  const [extraDecisions, setExtraDecisions] = useState([]);
  const [isAddingExtra, setIsAddingExtra] = useState(false);
  const lastEditSyncKeyRef = useRef(null);

  const editSyncKey = useMemo(() => (
    JSON.stringify({
      statusName,
      requestStatusChangeReasonCodes: (requestStatusChangeReasonCodes || []).map((rc) => ({
        reasonId: rc.reasonId,
        lenderRequestChange: rc.lenderRequestChange,
        status: rc.status || null,
      })),
    })
  ), [requestStatusChangeReasonCodes, statusName]);

  useEffect(() => {
    // When in read-only mode with persisted decisions, load them
    if (showDecisionStatus && persistedReasonCodeDecisions && persistedReasonCodeDecisions.length > 0) {
      lastEditSyncKeyRef.current = null;
      const acceptedIds = new Set();
      const extraDecisionsList = [];
      const requestedReasonIds = new Set((requestStatusChangeReasonCodes || []).map(rc => rc.reasonId));

      persistedReasonCodeDecisions.forEach(decision => {
        if (decision.status === LINE_ITEM_STATUS.ACCEPT) {
          acceptedIds.add(decision.reasonCodeId);
        }
        
        // If this decision is NOT in requestStatusChangeReasonCodes, it's an extra decision
        if (!requestedReasonIds.has(decision.reasonCodeId)) {
          // Extra decisions (Modify status) should always show as ADDED
          extraDecisionsList.push({
            reasonId: decision.reasonCodeId,
            lenderRequestChange: LENDER_REQUEST_CHANGE.ADDED,
          });
        }
      });

      setAcceptedRequestedIds(acceptedIds);
      setExtraDecisions(extraDecisionsList);
    } else if (!showDecisionStatus) {
      if (lastEditSyncKeyRef.current !== editSyncKey) {
        lastEditSyncKeyRef.current = editSyncKey;
        setAcceptedRequestedIds(buildAcceptedRequestedIds(requestStatusChangeReasonCodes, statusName));
      }
    } else if (showDecisionStatus && !persistedReasonCodeDecisions) {
      lastEditSyncKeyRef.current = null;
      // When in read-only mode without persisted decisions, compute from requestStatusChangeReasonCodes
      const acceptedIds = new Set();
      (requestStatusChangeReasonCodes || []).forEach(rc => {
        if (rc.status === LINE_ITEM_STATUS.ACCEPT) {
          acceptedIds.add(rc.reasonId);
        }
      });
      setAcceptedRequestedIds(acceptedIds);
      setExtraDecisions([]); // No extra decisions if not persisted
    }
  }, [showDecisionStatus, requestStatusChangeReasonCodes, persistedReasonCodeDecisions, statusName, editSyncKey]);

  const decidedReasonCodeIds = useMemo(
    () => computeFinalReasonCodeIds(activeReasonCodeIds, requestStatusChangeReasonCodes),
    [activeReasonCodeIds, requestStatusChangeReasonCodes]
  );

  const reviewingDecisions = useMemo(() => {
    if (showDecisionStatus) {
      // Read-only mode: use persisted status directly from the API
      const requestedDecisions = (requestStatusChangeReasonCodes || []).map(rc => ({
        reasonId: rc.reasonId,
        lenderRequestChange: rc.lenderRequestChange,
        status: rc.status,
      }));

      return requestedDecisions;
    } else {
      // Edit mode: reconstruct status based on acceptedRequestedIds to reflect checkbox changes
      let requestedDecisions = [];
      
      requestedDecisions = (requestStatusChangeReasonCodes || []).map(rc => ({
        reasonId: rc.reasonId,
        lenderRequestChange: rc.lenderRequestChange,
        status: acceptedRequestedIds.has(rc.reasonId) ? LINE_ITEM_STATUS.ACCEPT : LINE_ITEM_STATUS.REJECT,
      }));

      const extraDecisionsWithStatus = extraDecisions.map(d => ({
        reasonId: d.reasonId,
        lenderRequestChange: d.lenderRequestChange,
        status: LINE_ITEM_STATUS.MODIFY,
      }));

      return [...requestedDecisions, ...extraDecisionsWithStatus];
    }
  }, [requestStatusChangeReasonCodes, acceptedRequestedIds, extraDecisions, showDecisionStatus]);

  const reviewingReasonCodeIds = useMemo(
    () => computeFinalReasonCodeIds(activeReasonCodeIds, reviewingDecisions),
    [activeReasonCodeIds, reviewingDecisions]
  );

  // Final Unavailable Reasons always shows the result after applying reviewer's decisions
  const finalReasonCodeIds = reviewingReasonCodeIds;

  const finalReasonCodes = useMemo(() => (
    finalReasonCodeIds
      .map(id => reasonCodeMap.get(id))
      .filter(Boolean)
      .sort(reasonCodeComparer)
  ), [finalReasonCodeIds, reasonCodeMap]);

  // Keep the parent synced with the current reviewer decisions
  useEffect(() => {
    if (onReasonCodeDecisionsChange) {
      onReasonCodeDecisionsChange(
        reviewingDecisions.map(d => ({
          reasonCodeId: d.reasonId,
          status: d.status,
          lenderRequestChange: d.lenderRequestChange,
        }))
      );
    }
  }, [onReasonCodeDecisionsChange, reviewingDecisions]);

  // Report final reason codes count to parent.
  useEffect(() => {
    if (onFinalReasonCodesCountChange) {
      onFinalReasonCodesCountChange(finalReasonCodes.length);
    }
  }, [finalReasonCodes, onFinalReasonCodesCountChange]);

  const alreadyDecidedIdSet = useMemo(() => {
    const ids = new Set((requestStatusChangeReasonCodes || []).map(rc => rc.reasonId));
    extraDecisions.forEach(d => ids.add(d.reasonId));
    return ids;
  }, [requestStatusChangeReasonCodes, extraDecisions]);

  const remainingReasonCodeOptions = useMemo(() => (
    (reasonCodeOptions || [])
      .filter(rc => !alreadyDecidedIdSet.has(rc.reasonId))
      .sort(reasonCodeComparer)
  ), [reasonCodeOptions, alreadyDecidedIdSet]);

  const showAddExtraControl = (canReviewReasonCodes && !showDecisionStatus || (showDecisionStatus && isInternal && (statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN))) && remainingReasonCodeOptions.length > 0;

  const handleAcceptToggle = (reasonId, isAccepted) => {
    setAcceptedRequestedIds((prevAcceptedIds) => {
      const newSet = new Set(prevAcceptedIds);
      if (isAccepted) newSet.add(reasonId);
      else newSet.delete(reasonId);
      return newSet;
    });
  };

  const handleAddExtraReasonCode = (reasonId) => {
    const reasonCode = reasonCodeMap.get(reasonId);
    if (!reasonCode) return;

    const lenderRequestChange = activeIdSet.has(reasonId) ? LENDER_REQUEST_CHANGE.REMOVED : LENDER_REQUEST_CHANGE.ADDED;
    setExtraDecisions((prev) => [...prev, { reasonId, lenderRequestChange }]);
    setIsAddingExtra(false);
  };

  const handleRemoveExtraReasonCode = (reasonId) => {
    setExtraDecisions((prev) => prev.filter((d) => d.reasonId !== reasonId));
  };

  return (
    <>
      <div className="review-reason-codes-section">
        {renderSectionHeader("Current Unavailable Reasons", ListAltOutlinedIcon, "current-reason-codes-tone")}
        {renderReasonCodeBadgeList(currentReasonCodes, "No reason codes currently active on project.")}
      </div>

      {requestedRemoveReasonCodes.length > 0 && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Requested Unavailable Reasons to Remove", RemoveCircleOutlineOutlinedIcon, "requested-remove-reason-codes-tone")}
        {renderReasonCodeBadgeList(requestedRemoveReasonCodes, "No reason codes requested for removal.")}
      </div>
      )}

      {requestedAddReasonCodes.length > 0 && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Requested Unavailable Reasons to Add", AddCircleOutlineOutlinedIcon, "requested-add-reason-codes-tone")}
        {renderReasonCodeBadgeList(requestedAddReasonCodes, "No reason codes requested to add.")}
      </div>
      )}

      {/* Request Actions section: shown only during editing mode (canReviewReasonCodes = true and showDecisionStatus = false) */}
      {(!showDecisionStatus && canReviewReasonCodes && (requestStatusChangeReasonCodes || []).length > 0) && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Request Actions", AssignmentOutlinedIcon, "final-reason-codes-tone")}

        {(!showDecisionStatus && (requestStatusChangeReasonCodes || []).length > 0) && (
          <Table className="review-reason-codes-table requested-decision-table" style={{ marginBottom: 0 }}>
            <TableHead>
              <TableRow>
                <TableCell className="reason-code-name-column">Reason Code</TableCell>
                <TableCell>Requested Change</TableCell>
                <TableCell className="checkbox-column">Accept</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(requestStatusChangeReasonCodes || []).map((rc) => {
                const reasonCode = reasonCodeMap.get(rc.reasonId);
                const isAccepted = acceptedRequestedIds.has(rc.reasonId);

                return (
                  <TableRow key={rc.reasonId}>
                    <TableCell className="reason-code-name-column">{reasonCode?.reasonCode || "-"}</TableCell>
                    <TableCell>{rc.lenderRequestChange}</TableCell>
                    <TableCell className="checkbox-column">
                      <Checkbox
                        checked={isAccepted}
                        onChange={(e) => handleAcceptToggle(rc.reasonId, e.target.checked)}
                        style={{ color: "#085280" }}
                        inputProps={{ "aria-label": `Accept ${reasonCode?.reasonCode || rc.reasonId}` }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {(!showDecisionStatus && canReviewReasonCodes && showAddExtraControl) && (
          <>
            {(extraDecisions.length > 0 || isAddingExtra) && (
              <Table className="review-reason-codes-table added-reason-codes-table" style={{ marginTop: 0 }}>
                <TableBody>
                  {extraDecisions.map((d) => {
                    const reasonCode = reasonCodeMap.get(d.reasonId);
                    return (
                      <TableRow key={d.reasonId}>
                        <TableCell className="reason-code-name-column">{reasonCode?.reasonCode || "-"}</TableCell>
                        <TableCell>{d.lenderRequestChange}</TableCell>
                        <TableCell className="cancel-add-reason-code-column">
                          <IconButton
                            size="small"
                            aria-label="Remove extra reason code"
                            onClick={() => handleRemoveExtraReasonCode(d.reasonId)}
                          >
                            <FaRegTrashAlt style={{ fill: "red" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isAddingExtra && (
                    <TableRow className="insert-reason-code-row">
                      <TableCell style={{ width: 'auto' }} colSpan={2}>
                        <select
                          className="add-reason-code-select"
                          value=""
                          aria-label="Select reason code to add or remove"
                          onChange={(e) => handleAddExtraReasonCode(Number(e.target.value))}
                        >
                          <option value="" disabled>Select a reason code...</option>
                          {remainingReasonCodeOptions.map((reasonCode) => (
                            <option key={reasonCode.reasonId} value={reasonCode.reasonId}>{reasonCode.reasonCode || "-"}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="cancel-add-reason-code-column">
                        <IconButton
                          size="small"
                          aria-label="Cancel adding reason code"
                          onClick={() => setIsAddingExtra(false)}
                        >
                          <FaRegTrashAlt style={{ fill: "red" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <Button
              className="add-reason-code-button"
              onClick={() => setIsAddingExtra(true)}
              disabled={remainingReasonCodeOptions.length === 0}
              style={{ display: isAddingExtra ? 'none' : undefined }}
            >
              + Add/Remove Reason Code
            </Button>
          </>
        )}
      </div>
      )}

      {/* Reviewed Actions section: shown in read-only mode for internal users after decision is made */}
      {(showDecisionStatus && isInternal && (statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN) && (requestStatusChangeReasonCodes || []).length > 0) && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Reviewed Actions", EditOutlinedIcon, "reviewed-actions-tone")}
        <Table className="review-reason-codes-table reviewed-actions-table">
          <TableHead>
            <TableRow>
              <TableCell className="reason-code-name-column">Reason Code</TableCell>
              <TableCell>Requested Change</TableCell>
              <TableCell>Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(requestStatusChangeReasonCodes || []).map((rc) => {
              const reasonCode = reasonCodeMap.get(rc.reasonId);
              return (
                <TableRow key={rc.reasonId}>
                  <TableCell className="reason-code-name-column">{reasonCode?.reasonCode || "-"}</TableCell>
                  <TableCell>{renderRequestedChangeIcon(rc.lenderRequestChange)}</TableCell>
                  <TableCell>{renderDecisionIcon(rc.status)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      {/* Final Unavailable Reasons section: shown in reviewing mode (showDecisionStatus=false) or when request is Completed/Declined (ACTION_TAKEN/NO_ACTION_TAKEN) */}
      {(!showDecisionStatus || statusName === REQUEST_STATUSES.ACTION_TAKEN || statusName === REQUEST_STATUSES.NO_ACTION_TAKEN) && (
      <div className="review-reason-codes-section">
        {renderSectionHeader("Final Unavailable Reasons", CheckCircleOutlineOutlinedIcon, "final-reason-codes-tone")}
        <div className="final-reason-codes-list">
          {renderReasonCodeList(finalReasonCodes, "No reason codes in Final Unavailable Reasons.", false)}
        </div>
      </div>
      )}
    </>
  );
};

ReasonCodesReviewSection.propTypes = {
  activeReasonCodeIds: PropTypes.arrayOf(PropTypes.number),
  requestStatusChangeReasonCodes: PropTypes.arrayOf(PropTypes.shape({
    reasonId: PropTypes.number,
    status: PropTypes.string,
    lenderRequestChange: PropTypes.string,
  })),
  reasonCodeOptions: PropTypes.arrayOf(PropTypes.shape({
    reasonId: PropTypes.number,
    reasonCode: PropTypes.string,
  })),
  showDecisionStatus: PropTypes.bool,
  canReviewReasonCodes: PropTypes.bool,
  onReasonCodeDecisionsChange: PropTypes.func,
  onFinalReasonCodesCountChange: PropTypes.func,
  persistedReasonCodeDecisions: PropTypes.arrayOf(PropTypes.shape({
    reasonCodeId: PropTypes.number,
    status: PropTypes.string,
    lenderRequestChange: PropTypes.string,
  })),
  statusName: PropTypes.string,
  isInternal: PropTypes.bool,
};

export default ReasonCodesReviewSection;
